import React, {FC, useEffect, useRef} from 'react';
import styled, {css} from 'styled-components';

import {useInitialEffect} from '../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {usePrevious} from '../../../srcCommon/helpers/hookHelpers';
import {SettingsAppsTabsEnum, SettingsAppsUrl} from '../../models/url/settingsUrlModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {SettingsPanelId} from '../../store/panels/panelStateBase';
import {SettingsAppsDetailsPageContainer} from './pages/details/settingsAppsDetailsPageContainer';
import {SettingsAppsAllPageContainer} from './pages/settingsAppsAllPageContainer';
import {SettingsAppsCategoriesPage} from './pages/settingsAppsCategoriesPage';
import {SettingsAppsCategoryPageContainer} from './pages/settingsAppsCategoryPageContainer';
import {SettingsAppsChannelsPageContainer} from './pages/settingsAppsChannelsPageContainer';
import {SettingsAppsCollectionsPageContainer} from './pages/settingsAppsCollectionsPageContainer';
import {SettingsAppsEnabledPageContainer} from './pages/settingsAppsEnabledPageContainer';
import {SettingsAppsPluginsPageContainer} from './pages/settingsAppsPluginsPageContainer';
import {SettingsAppsPopularPageContainer} from './pages/settingsAppsPopularPageContainer';
import {SettingsAppsHeaderContainer} from './settingsAppsHeaderContainer';

/*
 * Props.
 */

interface SettingsAppsProps {
  panelId: SettingsPanelId;
  url: SettingsAppsUrl;
  isIntegrationTypeEnabled: boolean;
  fetchIntegrationTypesAsync: Handler;
  fetchAllIntegrationsAsync: Handler;
}

/*
 * Style.
 */

interface StyledSettingsAppsContentWrapperDivProps {
  $isRenderingTabs: boolean;
}

const StyledSettingsAppsContentWrapperDiv = styled.div<StyledSettingsAppsContentWrapperDivProps>`
  padding: 0 48px;
  overflow: auto;
  margin-top: -28px;
  padding-top: 28px;
  height: calc(100% - 82px);
  position: relative;
  min-width: 500px;

  ${(p) =>
    p.$isRenderingTabs &&
    css`
      height: calc(100% - 149px);
    `};
`;

const StyledSettingsMaxWidthWrapperDiv = styled.div`
  max-width: 1920px;
  display: flex;
  flex-direction: column;
`;

/*
 * Component.
 */

export const SettingsApps: FC<SettingsAppsProps> = (props) => {
  const {url, isIntegrationTypeEnabled, fetchIntegrationTypesAsync, fetchAllIntegrationsAsync} = props;

  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const previousUrl = usePrevious(url);

  useInitialEffect(() => {
    fetchIntegrationTypesAsync();
    fetchAllIntegrationsAsync();

    trackInterfaceEvent(InterfaceEventsEnum.APPS_SETTINGS_PAGE_VIEW, {
      settings_page: 'integrations',
      source: url.source,
    });
  });

  useEffect(() => {
    // If the tab page changes or the search query changes we should scroll to the top.
    if (
      (url.tabPage !== previousUrl?.tabPage || url.searchQuery !== previousUrl.searchQuery) &&
      contentWrapperRef.current
    ) {
      contentWrapperRef.current.scrollTo({top: 0});
    }
  }, [previousUrl?.searchQuery, previousUrl?.tabPage, url.searchQuery, url.tabPage]);

  return (
    <>
      <SettingsAppsHeaderContainer url={url} />
      <StyledSettingsAppsContentWrapperDiv
        ref={contentWrapperRef}
        $isRenderingTabs={computeIsRenderingTabs(url, isIntegrationTypeEnabled)}
      >
        <StyledSettingsMaxWidthWrapperDiv>{renderContent(url)}</StyledSettingsMaxWidthWrapperDiv>
      </StyledSettingsAppsContentWrapperDiv>
    </>
  );
};

/*
 * Helpers.
 */

function renderContent(url: SettingsAppsUrl) {
  switch (url.tabPage) {
    case SettingsAppsTabsEnum.COLLECTIONS:
      return <SettingsAppsCollectionsPageContainer url={url} />;
    case SettingsAppsTabsEnum.CATEGORIES: {
      if (url.category) {
        return <SettingsAppsCategoryPageContainer url={url} category={url.category} />;
      }
      return <SettingsAppsCategoriesPage />;
    }
    case SettingsAppsTabsEnum.CHANNELS:
      return <SettingsAppsChannelsPageContainer url={url} />;
    case SettingsAppsTabsEnum.ENABLED:
      return <SettingsAppsEnabledPageContainer url={url} />;
    case SettingsAppsTabsEnum.POPULAR:
      return <SettingsAppsPopularPageContainer url={url} />;
    case SettingsAppsTabsEnum.PLUGINS:
      return <SettingsAppsPluginsPageContainer url={url} />;
    case SettingsAppsTabsEnum.ALL:
      return <SettingsAppsAllPageContainer url={url} />;
    case SettingsAppsTabsEnum.DETAILS:
      return <SettingsAppsDetailsPageContainer url={url} />;
    default:
      return null;
  }
}

function computeIsRenderingTabs(url: SettingsAppsUrl, isIntegrationTypeEnabled: boolean) {
  const isInCategoriesSubPage = url.tabPage === SettingsAppsTabsEnum.CATEGORIES && url.category;
  const isInEnabledDetailsPage = url.tabPage === SettingsAppsTabsEnum.DETAILS && !isIntegrationTypeEnabled;
  return !isInCategoriesSubPage && !isInEnabledDetailsPage;
}
